import React, { useEffect, useState } from "react";
import axios from "axios";
import authConfig from "../../authConfig";
import { Form, Button, Row, Col, Alert, Spinner, Card } from "react-bootstrap";

/*
  FormRenderer
  - props:
    - formId (Guid) required
    - onSubmitted(submission) optional callback called after successful submit
      submission: { MemberFormId, heading, schema, values }
  Behavior
  - loads form via cookie-auth aware axios from authConfig.api.urls.getMemberFormForMember + formId
  - splits the form into pages by sections (if no sections then single page)
  - validates required fields per page; prevents moving forward if missing
  - submits to authConfig.api.urls.getMemberFormSubmit
  - displays heading/subheading and per-section subtitles (HTML)
  - added: question numbering for each field (sequential across the whole form)
  - changed: highlights fields that fail required validation
*/
export default function FormRenderer({ formId, onSubmitted = () => { } }) {
    const [loading, setLoading] = useState(false);
    const [submitLoading, setSubmitLoading] = useState(false);
    const [error, setError] = useState(null);
    const [successMsg, setSuccessMsg] = useState(null);

    // form data from server
    const [formMeta, setFormMeta] = useState({
        id: null,
        version: 1,
        heading: "",
        subtitle: "",
        schema: [],
        sections: [],
        sectionsubtitles: [],
        values: {}
    });

    const [pageIdx, setPageIdx] = useState(0);
    const [values, setValues] = useState({});
    // Track invalid fields: { [fieldName]: "message" }
    const [invalidFields, setInvalidFields] = useState({});

    useEffect(() => {
        if (!formId) return;
        loadForm();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formId]);

    // For cookie-based auth we simply ensure axios sends credentials (cookies).
    async function acquireAxios() {
        return axios.create({
            withCredentials: true
        });
    }

    async function loadForm() {
        setLoading(true);
        setError(null);
        setSuccessMsg(null);
        try {
            const client = await acquireAxios();
            const res = await client.get(authConfig.api.urls.getMemberFormForMember + formId);
            const data = res.data;
            console.log(data);
            const schema = data.formSchemaJson ? JSON.parse(data.formSchemaJson) : [];
            console.log(schema);
            const sections = data.sectionsJson ? JSON.parse(data.sectionsJson) : [];
            console.log(sections);
            const sectionsubtitles = data.sectionSubtitlesJson ? JSON.parse(data.sectionSubtitlesJson) : [];
            const initValues = data.valuesJson ? JSON.parse(data.valuesJson) : {};
            setFormMeta({
                id: data.id,
                version: data.version,
                heading: data.heading,
                subtitle: data.subHeading,
                schema,
                sections,
                sectionsubtitles,
                values: initValues
            });
            setValues(initValues || {});
            setPageIdx(0);
            setInvalidFields({});
        } catch (err) {
            console.error(err);
            setError("Error loading form: " + (err?.message || err));
        } finally {
            setLoading(false);
        }
    }

    // Build pages: each section is a page; if no sections, one page with all fields
    const pages = (() => {
        if (!formMeta.schema || formMeta.schema.length === 0) return [{ name: "", fields: [] }];
        if (formMeta.sections && formMeta.sections.length > 0) {
            return formMeta.sections.map((s, i) => ({
                name: s,
                subtitle: formMeta.sectionsubtitles?.[i] || "",
                fields: formMeta.schema.filter(f => (f.section || "") === s)
            }));
        } else {
            return [{ name: "", subtitle: "", fields: formMeta.schema }];
        }
    })();

    // Attach a sequential question number to each field (sequential across form)
    (() => {
        let counter = 0;
        for (const p of pages) {
            p.fields = p.fields.map(f => ({ ...f, __questionNumber: ++counter }));
        }
    })();

    const currentPage = pages[pageIdx] || { fields: [] };

    const handleChange = (e, field) => {
        const { name, type, checked, value } = e.target;
        const newVal = type === "checkbox" ? checked : value;
        setValues(prev => ({ ...prev, [name]: newVal }));
        // clear invalid marker for this field when user changes it
        if (invalidFields[name]) {
            setInvalidFields(prev => {
                const copy = { ...prev };
                delete copy[name];
                return copy;
            });
            setError(null);
        }
    };

    // Validate a page's required fields. Marks invalid fields in state and returns result.
    const validatePage = (page) => {
        const newInvalids = {};
        for (const f of page.fields) {
            if (f.required) {
                const v = values[f.name];
                if (f.type === "checkbox") {
                    if (!v) newInvalids[f.name] = `Please check "${f.label}"`;
                } else {
                    if (v === undefined || v === null || String(v).trim() === "") {
                        newInvalids[f.name] = `Please enter "${f.label}"`;
                    }
                }
            }
        }

        if (Object.keys(newInvalids).length > 0) {
            setInvalidFields(newInvalids);
            const firstName = Object.keys(newInvalids)[0];
            return { ok: false, message: newInvalids[firstName], firstFieldName: firstName };
        }

        // no invalids
        setInvalidFields(prev => {
            // remove any invalid markers for fields on this page only
            const copy = { ...prev };
            for (const f of page.fields) {
                if (copy[f.name]) delete copy[f.name];
            }
            return copy;
        });
        return { ok: true };
    };

    const goNext = () => {
        const valid = validatePage(currentPage);
        if (!valid.ok) {
            setError(valid.message);
            // focus first invalid field if present
            try {
                const el = document.getElementsByName(valid.firstFieldName)[0];
                if (el && typeof el.focus === "function") el.focus();
            } catch { }
            return;
        }
        setError(null);
        setPageIdx(p => Math.min(p + 1, pages.length - 1));
    };

    const goPrev = () => {
        setError(null);
        setPageIdx(p => Math.max(p - 1, 0));
    };

    const submitForm = async () => {
        // validate final page and also optionally validate all required fields across entire form
        // Here validate all pages to ensure nothing missed
        for (const p of pages) {
            const v = validatePage(p);
            if (!v.ok) {
                setError(v.message);
                // navigate to that page
                const idx = pages.indexOf(p);
                setPageIdx(idx >= 0 ? idx : pageIdx);
                // focus first invalid field
                try {
                    const el = document.getElementsByName(v.firstFieldName)[0];
                    if (el && typeof el.focus === "function") el.focus();
                } catch { }
                return;
            }
        }
        setError(null);
        setSubmitLoading(true);
        try {
            const client = await acquireAxios();
            const payload = {
                MemberFormId: formMeta.id,
                FormVersion: formMeta.version || 1,
                SubmissionDataJson: JSON.stringify(values || {})
            };
            await client.post(authConfig.api.urls.getMemberFormSubmit, payload);

            // build a submission summary to pass to parent
            const submission = {
                MemberFormId: formMeta.id,
                heading: formMeta.heading,
                schema: formMeta.schema,
                values: { ...(values || {}) }
            };

            setSuccessMsg("Form submitted successfully");
            setValues({});
            setInvalidFields({});
            // Notify parent with submission details (parent can show a summary)
            onSubmitted(submission);
        } catch (err) {
            console.error(err);
            setError("Error submitting form: " + (err?.message || err));
        } finally {
            setSubmitLoading(false);
        }
    };

    if (loading) return <div className="text-center"><Spinner animation="border" /></div>;
    if (!formId) return <Alert variant="warning">No form selected</Alert>;

    return (
        <Card className="mb-3">
            <Card.Body>
                {error && <Alert variant="danger" onClose={() => setError(null)} dismissible>{error}</Alert>}
                {successMsg && <Alert variant="success" onClose={() => setSuccessMsg(null)} dismissible>{successMsg}</Alert>}

                <h3>{formMeta.heading || "Form"}</h3>
                {pageIdx === 0 && formMeta.subtitle ? (<div className="text-muted mb-3" dangerouslySetInnerHTML={{ __html: formMeta.subtitle }} />) : ""}

                <div className="mb-2"><strong>Section {pageIdx + 1} of {pages.length}</strong></div>

                {currentPage.name && <h4>{currentPage.name}</h4>}
                {currentPage.subtitle && <div className="text-muted mb-2" dangerouslySetInnerHTML={{ __html: currentPage.subtitle }} />}

                <Form>
                    {currentPage.fields.map((field, i) => {
                        const val = values[field.name] ?? (field.type === "checkbox" ? false : "");
                        const qnum = field.__questionNumber;
                        const labelNode = (
                            <>
                                <span style={{ marginRight: 8, fontWeight: 600 }}>{qnum}.</span>
                                <span>{field.label}</span>
                                {field.required && <span style={{ color: "red" }}> *</span>}
                            </>
                        );

                        const fieldError = invalidFields[field.name];

                        // Render checkbox as inline control with label included in the control
                        if (field.type === "checkbox") {
                            return (
                                <Form.Group className="mb-3" key={field.name || i}>
                                    <Form.Check
                                        type="checkbox"
                                        name={field.name}
                                        label={labelNode}
                                        checked={!!val}
                                        onChange={(e) => handleChange(e, field)}
                                        className={fieldError ? "is-invalid" : undefined}
                                    />
                                    {field.subtitle && <div className="text-muted mt-1" dangerouslySetInnerHTML={{ __html: field.subtitle }} />}
                                    {fieldError && <div className="invalid-feedback d-block">{fieldError}</div>}
                                </Form.Group>
                            );
                        }

                        // For other controls render label above the control (including radio)
                        return (
                            <Form.Group className="mb-3" key={field.name || i}>
                                <Form.Label>
                                    {labelNode}
                                </Form.Label>
                                {field.subtitle && <div className="text-muted mb-1" dangerouslySetInnerHTML={{ __html: field.subtitle }} />}

                                {field.type === "select" ? (
                                    <>
                                        <Form.Select name={field.name} value={val} onChange={(e) => handleChange(e, field)} required={field.required} isInvalid={!!fieldError}>
                                            <option value="">Select...</option>
                                            {field.options?.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                                        </Form.Select>
                                        {fieldError && <Form.Control.Feedback type="invalid">{fieldError}</Form.Control.Feedback>}
                                    </>
                                ) : field.type === "radio" ? (
                                    <>
                                        {field.options?.map((opt, idx) => (
                                            <Form.Check
                                                inline
                                                key={idx}
                                                type="radio"
                                                name={field.name}
                                                label={opt.label}
                                                value={opt.value}
                                                checked={values[field.name] === opt.value}
                                                onChange={(e) => handleChange(e, field)}
                                            />
                                        ))}
                                        {fieldError && <div className="invalid-feedback d-block">{fieldError}</div>}
                                    </>
                                ) : field.type === "textarea" ? (
                                    <>
                                        <Form.Control as="textarea" rows={3} name={field.name} value={val} onChange={(e) => handleChange(e, field)} required={field.required} isInvalid={!!fieldError} />
                                        {fieldError && <Form.Control.Feedback type="invalid">{fieldError}</Form.Control.Feedback>}
                                    </>
                                ) : (
                                    <>
                                        <Form.Control type={field.type || "text"} name={field.name} value={val} onChange={(e) => handleChange(e, field)} required={field.required} isInvalid={!!fieldError} />
                                        {fieldError && <Form.Control.Feedback type="invalid">{fieldError}</Form.Control.Feedback>}
                                    </>
                                )}
                            </Form.Group>
                        );
                    })}

                    <Row className="mt-3">
                        <Col>
                            <Button variant="secondary" onClick={goPrev} disabled={pageIdx === 0}>Previous</Button>
                        </Col>
                        <Col className="text-end">
                            {pageIdx < pages.length - 1 ? (
                                <Button variant="primary" onClick={goNext}>Next</Button>
                            ) : (
                                <Button variant="success" onClick={submitForm} disabled={submitLoading}>
                                    {submitLoading ? (<><Spinner animation="border" size="sm" /> Submitting...</>) : "Submit"}
                                </Button>
                            )}
                        </Col>
                    </Row>
                </Form>
            </Card.Body>
        </Card>
    );
}